import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

type FieldRowContext = {
  /** ヒントを持つ Field が自分を登録する。返り値は解除。 */
  registerHint: () => () => void;
};

/** 中身が `FieldRow` の行内に居るか。null なら縦積み (既定)。 */
const fieldRowContext = createContext<FieldRowContext | null>(null);

/**
 * 横並びのフォーム 1 行。
 *
 * 入力欄の高さは揃っているので `items-end` で下端を合わせれば一直線になる。
 * ただしヒント付きの `Field` はヒントの分だけ背が高くなり、その 1 つだけ
 * 入力欄が上に浮いてしまう。そこで行内の `Field` にはヒントを絶対配置で
 * 垂らさせ (箱の高さに影響しない)、代わりに垂れた分の余白をこの行が持つ。
 *
 * inline の伝達は context で行う。call サイトの各 `Field` に印を書かせると
 * 付け忘れがそのままガタつきになるため。
 */
export function FieldRow({ children, className }: { children: ReactNode; className?: string }) {
  const [hintCount, setHintCount] = useState(0);
  const context = useMemo<FieldRowContext>(
    () => ({
      registerHint: () => {
        setHintCount((count) => count + 1);
        return () => setHintCount((count) => count - 1);
      },
    }),
    [],
  );

  return (
    <fieldRowContext.Provider value={context}>
      <div
        className={cn(
          'flex flex-wrap items-end gap-x-4',
          // 垂れたヒントの高さ (mt-1 + text-xs/leading-relaxed) 分だけ下と行間を空ける。
          // 768px 未満ではヒントを垂らさない (下の Field を参照) ので、その余白も要らない。
          hintCount > 0 ? 'gap-y-4 md:gap-y-10 md:pb-6' : 'gap-y-4',
          className,
        )}
      >
        {children}
      </div>
    </fieldRowContext.Provider>
  );
}

/** ラベル + 説明 + 入力。フォームの縦の間隔をここで揃える。 */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const row = useContext(fieldRowContext);
  const inline = row !== null;
  const hasHint = Boolean(hint);

  useEffect(() => {
    if (!row || !hasHint) return;
    return row.registerHint();
  }, [row, hasHint]);

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children でコントロールを包む暗黙の関連付け。中身は呼び出し側が渡すので Biome からは見えない
    <label className={cn('flex flex-col gap-1.5', inline && 'relative', className)}>
      <span className="text-xs font-medium text-ink-secondary">{label}</span>
      {children}
      {hint ? (
        <span
          className={cn(
            'text-xs leading-relaxed text-ink-subtle',
            /*
             * 垂らすのは 768px 以上だけ。折り返さないヒント (`whitespace-nowrap`)
             * は、狭い幅ではそのままページを横に押し広げてしまう。
             * 768px 未満では行の中に流し込む——行はもともと `flex-wrap` なので、
             * 入力欄の下に素直に収まる。
             */
            inline && 'md:absolute md:top-full md:left-0 md:mt-1 md:whitespace-nowrap',
          )}
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

/**
 * 入力欄と同じ高さの箱。
 *
 * トグルのように背の低いコントロールを行に置くとき、これに載せると
 * 入力ラインの中心に揃う (決め打ちの `pb-*` でズラさない)。
 * 高さは Input / Select と同じ py-2 + 1px ボーダー + text-sm の行送り。
 */
export function FieldControl({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex h-9.5 items-center', className)}>{children}</div>;
}
